import React, { useEffect, useState, useContext, useRef } from 'react';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import brLocale from 'date-fns/locale/pt-BR';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { Button, Grid, TextField } from '@material-ui/core';
import Typography from "@material-ui/core/Typography";
import api from '../../services/api';
import { format } from 'date-fns';
import { toast } from 'react-toastify';
import './button.css';
import { i18n } from '../../translate/i18n';
import { AuthContext } from "../../context/Auth/AuthContext";
import { useTheme } from '@material-ui/core';

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend
);

const BLUE = '#3b82f6';

function createGradient(ctx, color) {
    const gradient = ctx.createLinearGradient(0, 0, 0, 300);
    gradient.addColorStop(0, color + 'cc');
    gradient.addColorStop(1, color + '22');
    return gradient;
}

export const ChartsDate = () => {
    const theme = useTheme();
    const chartRef = useRef(null);
    const [initialDate, setInitialDate] = useState(new Date());
    const [finalDate, setFinalDate] = useState(new Date());
    const [ticketsData, setTicketsData] = useState({ data: [], count: 0 });
    const [gradientBg, setGradientBg] = useState(BLUE);
    const { user } = useContext(AuthContext);

    const companyId = user.companyId;

    useEffect(() => {
        if (companyId) {
            handleGetTicketsInformation();
        }
    }, [companyId]);

    useEffect(() => {
        const chart = chartRef.current;
        if (chart) {
            setGradientBg(createGradient(chart.ctx, BLUE));
        }
    }, [ticketsData]);

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { display: false },
            title: { display: false },
            tooltip: {
                backgroundColor: '#ffffff',
                titleColor: '#374151',
                bodyColor: '#6b7280',
                borderColor: '#e5e7eb',
                borderWidth: 1,
                cornerRadius: 8,
                padding: 12,
                displayColors: false,
                callbacks: {
                    title: (items) => items[0]?.label || '',
                    label: (item) => `${item.parsed.y} atendimento${item.parsed.y !== 1 ? 's' : ''}`,
                },
            },
        },
        scales: {
            x: {
                grid: { display: false },
                border: { display: false },
                ticks: { color: '#9ca3af', font: { size: 11, family: 'inherit' } },
            },
            y: {
                grid: { color: 'rgba(0,0,0,0.05)', drawBorder: false },
                border: { display: false, dash: [4, 4] },
                ticks: {
                    color: '#9ca3af',
                    font: { size: 11, family: 'inherit' },
                    precision: 0,
                },
            },
        },
    };

    const dataCharts = {
        labels: ticketsData?.data.length > 0
            ? ticketsData.data.map((item) =>
                item.hasOwnProperty('horario')
                    ? `${item.horario}h`
                    : item.data
            )
            : [],
        datasets: [
            {
                data: ticketsData?.data.length > 0
                    ? ticketsData.data.map((item) => item.total)
                    : [],
                backgroundColor: gradientBg,
                borderRadius: 6,
                borderSkipped: false,
                borderWidth: 0,
                hoverBackgroundColor: BLUE,
            },
        ],
    };

    const handleGetTicketsInformation = async () => {
        try {
            const { data } = await api.get(
                `/dashboard/ticketsDay?initialDate=${format(initialDate, 'yyyy-MM-dd')}&finalDate=${format(finalDate, 'yyyy-MM-dd')}&companyId=${companyId}`
            );
            setTicketsData(data);
        } catch (error) {
            toast.error('Erro ao buscar informações dos tickets');
        }
    };

    return (
        <>
            <Typography component="h2" variant="h6" color="primary" gutterBottom>
                {i18n.t("dashboard.users.totalAttendances")} ({ticketsData?.count})
            </Typography>

            <Grid container spacing={2} style={{ marginBottom: 16 }}>
                <Grid item>
                    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={brLocale}>
                        <DatePicker
                            value={initialDate}
                            onChange={(newValue) => { setInitialDate(newValue); }}
                            label={i18n.t("dashboard.date.initialDate")}
                            renderInput={(params) => <TextField fullWidth {...params} sx={{ width: '20ch' }} />}
                        />
                    </LocalizationProvider>
                </Grid>
                <Grid item>
                    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={brLocale}>
                        <DatePicker
                            value={finalDate}
                            onChange={(newValue) => { setFinalDate(newValue); }}
                            label={i18n.t("dashboard.date.finalDate")}
                            renderInput={(params) => <TextField fullWidth {...params} sx={{ width: '20ch' }} />}
                        />
                    </LocalizationProvider>
                </Grid>
                <Grid item>
                    <Button
                        style={{ backgroundColor: theme.palette.primary.main, top: '10px' }}
                        onClick={handleGetTicketsInformation}
                        variant="contained"
                    >
                        Filtrar
                    </Button>
                </Grid>
            </Grid>

            <div style={{ height: 260 }}>
                <Bar ref={chartRef} options={options} data={dataCharts} />
            </div>
        </>
    );
};
